/**
 * Hook for AI text polishing with state management and error handling.
 *
 * @example
 * ```tsx
 * const polish = useTextPolish({
 *   fieldContext: 'Problem Description',
 *   customPrompt: 'Focus on technical accuracy',
 *   onSuccess: (polished, original) => {
 *     setModalData({ polished, original });
 *     setShowModal(true);
 *   }
 * });
 *
 * // Later:
 * <button onClick={() => polish.polishText(fieldValue)}>
 *   Polish Text
 * </button>
 * ```
 */

import { useState, useCallback } from 'react';
import { fetchJsonWithRetry, describeFetchFailure } from '../../lib/utils/transientError';
import { usePublicFormContext } from '../../components/forms/public/PublicFormContext';

export type PolishState = 'idle' | 'processing' | 'success' | 'error';

interface UseTextPolishOptions {
  fieldContext?: string;
  customPrompt?: string;
  tone?: 'professional' | 'technical' | 'casual';
  onSuccess: (polishedText: string, originalText: string) => void;
}

interface UseTextPolishReturn {
  state: PolishState;
  error: string | null;
  polishedText: string | null;
  originalText: string | null;
  processingTime: number | null;
  polishText: (text: string) => Promise<void>;
  reset: () => void;
}

export function useTextPolish({
  fieldContext,
  customPrompt,
  tone = 'professional',
  onSuccess,
}: UseTextPolishOptions): UseTextPolishReturn {
  const [state, setState] = useState<PolishState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [polishedText, setPolishedText] = useState<string | null>(null);
  const [originalText, setOriginalText] = useState<string | null>(null);
  const [processingTime, setProcessingTime] = useState<number | null>(null);
  const { getAiCredential } = usePublicFormContext();

  const polishText = useCallback(async (text: string) => {
    if (!text.trim()) {
      setError('Text is empty');
      setState('error');
      return;
    }

    setState('processing');
    setError(null);

    try {
      const API_BASE_URL =
        process.env.REACT_APP_FUNCTIONS_URL ||
        'https://us-central1-quoter-saas.cloudfunctions.net';

      // Retries transient failures (cold start, timeout, 429/502/503/504) automatically
      // before surfacing anything to the user — see #2360.
      const data = await fetchJsonWithRetry<{ success?: boolean; polishedText?: string; originalText?: string; processingTime?: number }>(
        `${API_BASE_URL}/forms_polishText`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await getAiCredential()) },
          body: JSON.stringify({
            text,
            fieldContext,
            customPrompt,
            tone,
          }),
          timeout: 35000, // 35s (Cloud Function 30s + buffer)
          // See useVoiceInput — a quota 429 must not be retried.
          retryOn429: false,
        }
      );

      if (!data.success || !data.polishedText) {
        throw new Error('AI returned empty response');
      }

      setPolishedText(data.polishedText);
      setOriginalText(data.originalText || text);
      setProcessingTime(data.processingTime ?? null);
      setState('success');
      onSuccess(data.polishedText, text);
    } catch (err) {
      // describeFetchFailure distinguishes a cold-start-adjacent transient failure
      // (already retried by fetchJsonWithRetry) from a real connectivity problem —
      // see #2360. Domain-specific messages below take priority when they apply.
      let message = describeFetchFailure(err);

      if (err instanceof Error) {
        if (err.message.includes('empty')) {
          message = 'AI returned empty response. Please try again.';
        } else if (err.message.includes('429') || err.message.includes('rate')) {
          message = 'Service busy. Please wait a moment and try again.';
        } else if (err.message.includes('Text too long')) {
          message = err.message;
        }
      }

      setError(message);
      setState('error');

      // Auto-clear error after 3 seconds
      setTimeout(() => {
        if (state === 'error') {
          setState('idle');
          setError(null);
        }
      }, 3000);
    }
  }, [fieldContext, customPrompt, tone, onSuccess, state, getAiCredential]);

  const reset = useCallback(() => {
    setState('idle');
    setError(null);
    setPolishedText(null);
    setOriginalText(null);
    setProcessingTime(null);
  }, []);

  return {
    state,
    error,
    polishedText,
    originalText,
    processingTime,
    polishText,
    reset,
  };
}
